/**
 * useStreak — calls bump_streak() once per session, exposes current/longest.
 * Listens for realtime updates so the badge stays fresh.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface StreakRow {
  user_id: string;
  current: number;
  longest: number;
  last_active: string | null;
  updated_at: string;
}

const SESSION_KEY = "wavlynq.streak.bumped";

export function useStreak() {
  const { user } = useAuth();
  const [streak, setStreak] = useState<StreakRow | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) {
      setStreak(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("streaks")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setStreak((data as StreakRow | null) ?? null);
    setLoading(false);
  }, [user]);

  // Bump once per browser session, then load
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const sessionKey = `${SESSION_KEY}:${user.id}:${today}`;
    const alreadyBumped =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(sessionKey) === "1";

    (async () => {
      if (!alreadyBumped) {
        const { data } = await supabase.rpc("bump_streak");
        if (data) setStreak(data as StreakRow);
        try {
          sessionStorage.setItem(sessionKey, "1");
        } catch {
          /* ignore storage errors */
        }
      }
      await reload();
    })();
  }, [user, reload]);

  return { streak, loading, reload };
}
